import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import { parse, stringify } from 'query-string';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { PhoneCallDirection } from '../../../extViewmodels';
import {
	AIAgentTemplateFilterProperty,
	AIAgentTemplateScope,
	AIConversationType,
} from '../../../extViewmodels/sdk/models/AI';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useAIAgentTemplateFilterInfiniteQuery } from '../../../queries/AIAgentTemplate/useAIAgentTemplateFilterInfiniteQuery';
import { useUserPreferencesMutation } from '../../../queries/User/useUserPreferencesMutation';
import { useEventLogging } from '../../../models/Logging';
import {
	AppDrawerContent,
	AppDrawerToolbarButton,
	CoffeeDrawerToolbar,
	SideDrawer,
	useAppDrawerContext,
} from '../../../web/components-v2/Drawer/AppDrawer';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../../web/components-v2/Dropdown';
import { Toggle } from '../../../web/components/Toggle';
import { SettingsIcon } from '../../../web/components/svgs/icons/SettingsIcon';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { baseStyleSheet } from '../../../web/styles/styles';
import { useTysonCoaching } from '../../voice-agent/contexts/TysonCoachingContext';
import { TysonCoachingPanel } from '../../voice-agent/components/TysonCoachingPanel';
import { TysonCoachingDropdown } from '../../voice-agent/components/TysonCoachingDropdown';
import { RealCallCoachingBridge } from '../../voice-agent/components/RealCallCoachingBridge';
import { usePreselectedScenario } from '../../contexts/preselectedScenario';
import { useQueueLeadNote } from '../../contexts/queueLeadNote';
import { useTysonMode } from '../../contexts/tysonMode';
import { SearchKeyOptions, useSearch } from '../../contexts/search';
import { DialerPhoneCallStatus, useTelephony } from '../../contexts/telephony';
import { useQueue } from '../../hooks/queue';
import { usePrefetchLeadAIContext } from '../../queries/LeadAIContext';
import { CallingCard } from '../CallingCard';
import { DollarSignIcon } from '../svgs/icons/DollarSignIcon';
import { WhoYaGonnaCall } from '../WhoYaGonnaCall';
import { DealDrawerContent } from './DealDrawerContent';
import { styleSheet } from './styles';

export const COFFEE_DRAWER_VALUE_DEAL = 'coffee-deal';
export const TYSON_COACHING_DRAWER_VALUE = 'tyson-coaching';
export const COACHING_SETTINGS_DRAWER_VALUE = 'coaching-settings';
const QueueBase = () => {
	const userSession = useUserSession();
	const queue = useQueue();
	const [previousCompanyId, setPreviousCompanyId] = useState(queue?.lead?.company?.id);
	const notes = useQueueLeadNote();
	const { phoneCalls, phoneCall, phoneCallStatus } = useTelephony();
	const { setShowSearch, setPayload } = useSearch();
	const [isLeadLocked, lockLead] = useState<boolean>(false);
	const { logInput } = useEventLogging('Queue');
	const appDrawerContext = useAppDrawerContext();
	const {
		isActive: isCoachingActive,
		stopCoaching,
		startCoaching,
		selectedCoachingTemplateId,
		setSelectedCoachingTemplateId,
	} = useTysonCoaching();
	const { isActive: isTysonModeActive } = useTysonMode();
	const realtimeCoachingEnabled =
		userSession.user?.userPreferences?.coffeePreferences?.realtimeCoachingEnabled ?? false;
	const autoSuggestionsEnabled = userSession.user?.userPreferences?.coffeePreferences?.autoSuggestionsEnabled ?? false;
	const { data: coachingTemplatePages } = useAIAgentTemplateFilterInfiniteQuery({
		filter: {
			criteria: [
				{ property: AIAgentTemplateFilterProperty.Scope, value: AIAgentTemplateScope.Account },
				{ property: AIAgentTemplateFilterProperty.ConversationType, value: AIConversationType.SalesLiveCoaching },
			],
		},
		enabled: realtimeCoachingEnabled,
	});
	const coachingTemplates = coachingTemplatePages?.pages.flatMap(page => page.values) ?? [];

	// Clear stale template selection if the saved template no longer exists
	useEffect(() => {
		if (
			selectedCoachingTemplateId &&
			coachingTemplates.length > 0 &&
			!coachingTemplates.find(t => t.id === selectedCoachingTemplateId)
		) {
			setSelectedCoachingTemplateId(null);
		}
	}, [coachingTemplates, selectedCoachingTemplateId, setSelectedCoachingTemplateId]);

	const updatePreferences = useUserPreferencesMutation({
		onSuccess: userPrefs => {
			userSession.user.userPreferences = userPrefs;
		},
		onError: error => {
			console.error('Failed to update coaching preference:', error);
		},
	});
	const showRealtimeCoachingUI = isTysonModeActive || realtimeCoachingEnabled;
	const prefetchLeadAIContext = usePrefetchLeadAIContext();
	const prevCompanyIdRef = useRef<string | null>(null);
	const coachingStartedForCompanyRef = useRef<string | null>(null);
	const leadServedIdTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Handle preselected scenario from URL query param
	const location = useLocation();
	const history = useHistory();
	const { setPreselectedScenarioId } = usePreselectedScenario();

	// When component mounts or location.search changes, check for scenarioId param
	useEffect(() => {
		const searchParams = parse(location.search);
		const scenarioIdParam = searchParams.scenarioId as string | undefined;

		if (scenarioIdParam) {
			setPreselectedScenarioId(scenarioIdParam);
			// Clear the query param after reading it to clean up the URL
			delete searchParams.scenarioId;
			const newSearch = stringify(searchParams);
			history.replace({
				pathname: location.pathname,
				search: newSearch ? `?${newSearch}` : '',
			});
		}
	}, [location.search, location.pathname, history, setPreselectedScenarioId]);

	// Detect lead change: stop coaching, prefetch data
	useEffect(() => {
		const currentCompanyId = queue?.companyId;
		const leadServedId = queue?.leadServedId;
		const leadChanged = currentCompanyId && currentCompanyId !== prevCompanyIdRef.current;

		if (!leadChanged) return;

		prevCompanyIdRef.current = currentCompanyId;
		// Reset coaching tracker so auto-start can fire for the new lead
		coachingStartedForCompanyRef.current = null;

		// Prefetch HubSpot data for current lead
		prefetchLeadAIContext(currentCompanyId, leadServedId).catch(error => {
			console.error('Failed to prefetch lead AI context:', error);
		});

		// Stop any existing coaching session when lead changes
		if (isCoachingActive) {
			stopCoaching();
		}
	}, [queue?.companyId, queue?.leadServedId, prefetchLeadAIContext, isCoachingActive, stopCoaching]);

	// Auto-start coaching for coffee accounts when all required data is available
	// Separated from lead change detection because contacts load asynchronously
	useEffect(() => {
		const currentCompanyId = queue?.companyId;
		const contactId = queue?.lead?.contacts?.[0]?.id;
		const leadServedId = queue?.leadServedId;
		const isCoffeeAccount = userSession.account.isLevitateSalesCoffeeAccount;

		// Don't auto-start if coaching UI isn't visible or not a coffee account
		if (!showRealtimeCoachingUI || !isCoffeeAccount || !currentCompanyId || !contactId) {
			return;
		}

		// Don't start again if already started for this lead
		if (coachingStartedForCompanyRef.current === currentCompanyId) {
			return;
		}

		// Don't start if there's already an active session (e.g. user manually started one)
		if (isCoachingActive) {
			return;
		}

		// If leadServedId is available, start immediately
		if (leadServedId) {
			coachingStartedForCompanyRef.current = currentCompanyId;
			startCoaching({ companyId: currentCompanyId, contactId, leadServedId });
			return;
		}

		// leadServedId not yet available — wait briefly then start without it
		// This handles direct access leads where leadServedId may never arrive
		if (leadServedIdTimeoutRef.current) {
			clearTimeout(leadServedIdTimeoutRef.current);
		}
		leadServedIdTimeoutRef.current = setTimeout(() => {
			// Re-check conditions — component may have changed leads during the wait
			const latestCompanyId = queue?.companyId;
			const latestContactId = queue?.lead?.contacts?.[0]?.id;
			const latestLeadServedId = queue?.leadServedId;

			if (
				latestCompanyId === currentCompanyId &&
				latestContactId &&
				coachingStartedForCompanyRef.current !== currentCompanyId
			) {
				coachingStartedForCompanyRef.current = currentCompanyId;
				startCoaching({
					companyId: currentCompanyId,
					contactId: latestContactId,
					leadServedId: latestLeadServedId,
				});
			}
		}, 2000);

		return () => {
			if (leadServedIdTimeoutRef.current) {
				clearTimeout(leadServedIdTimeoutRef.current);
			}
		};
	}, [
		queue?.companyId,
		queue?.leadServedId,
		queue?.lead?.contacts,
		startCoaching,
		isCoachingActive,
		showRealtimeCoachingUI,
		userSession.account.isLevitateSalesCoffeeAccount,
	]);

	// If the company id changes, reset the selected drawer value to close the drawer
	const companyId = queue?.lead?.company?.id;
	const { setValue: setDrawerValue } = appDrawerContext;
	useEffect(() => {
		// Skip the initial render — only reset drawer on actual company changes
		if (previousCompanyId != null && previousCompanyId !== companyId) {
			setDrawerValue('');
		}
		setPreviousCompanyId(companyId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyId, setDrawerValue]);

	const onGlobalKeydown = (e: KeyboardEvent) => {
		if (e.key === '/' && (e.ctrlKey || e.metaKey)) {
			e.stopPropagation();
			e.preventDefault();
			notes.setShowQueueLeadNote(!notes.showQueueLeadNote);
		}
	};

	useEffect(() => {
		window.removeEventListener('keydown', onGlobalKeydown);
		window.addEventListener('keydown', onGlobalKeydown);
		return () => {
			window.removeEventListener('keydown', onGlobalKeydown);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [notes.showQueueLeadNote]);

	useEffect(() => {
		lockLead(false);

		phoneCalls.clearCurrentCall();
		if (queue?.lead?.pendingOutcome?.phoneCall) {
			phoneCalls.setExistingCall(queue.lead.pendingOutcome.phoneCall, queue.companyId);
		}

		if (queue?.companyId) {
			queue.loadPhoneCallOutcomes(queue.companyId);
			queue.loadSkipReasons(queue.companyId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queue?.companyId]);

	useEffect(() => {
		if (phoneCall?.direction === PhoneCallDirection.Outbound && queue) {
			queue.hasCalled = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phoneCall?.id]);

	useEffect(() => {
		if (
			phoneCall?.direction === PhoneCallDirection.Inbound &&
			!phoneCall?.companyId &&
			phoneCallStatus === DialerPhoneCallStatus.Connected
		) {
			lockLead(true);
			setPayload({ key: SearchKeyOptions.CallFromUnknown });
			setShowSearch(true);
		} else {
			lockLead(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phoneCall?.id, phoneCallStatus]);

	// Open drawer when Tyson coaching becomes active (only on activation, not continuously)
	const prevCoachingActiveRef = useRef(false);
	useEffect(() => {
		if (showRealtimeCoachingUI && isCoachingActive && !prevCoachingActiveRef.current) {
			appDrawerContext.setValue(TYSON_COACHING_DRAWER_VALUE);
		}
		prevCoachingActiveRef.current = isCoachingActive;
	}, [isCoachingActive, appDrawerContext, showRealtimeCoachingUI]);

	const sunShade = (
		<div className={css(styleSheet.sunShade)}>
			<p>You are in a call with an unrecognized contact/company.</p>
			<p>If you know who you are talking to, you can search for the lead and load it.</p>
		</div>
	);

	const renderLayer1 = () => {
		if (!queue || queue.isBusy) {
			return <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />;
		}

		if (isLeadLocked) {
			// you are in a call with a different company than the one loaded
			return sunShade;
		}

		if (!queue.lead) {
			return <WhoYaGonnaCall />;
		}

		return <CallingCard />;
	};

	return (
		<div className={css(styleSheet.root)}>
			<div className={css(styleSheet.queueRoot)}>
				<div className={css(styleSheet.queueContainer)}>
					<div className={css(styleSheet.layer, styleSheet.transparentLayer, styleSheet.layer3)} />
					<div className={css(styleSheet.layer, styleSheet.transparentLayer, styleSheet.layer2)} />
					<div className={css(styleSheet.layer, styleSheet.layer1)}>{renderLayer1()}</div>
				</div>
			</div>
			<SideDrawer styles={[styleSheet.drawer]}>
				<AppDrawerContent value={COFFEE_DRAWER_VALUE_DEAL} styles={[styleSheet.drawerContent]} unmountOnExit>
					<DealDrawerContent onClose={() => appDrawerContext.setValue('')} />
				</AppDrawerContent>
				{showRealtimeCoachingUI && (
					<AppDrawerContent value={TYSON_COACHING_DRAWER_VALUE} styles={[styleSheet.drawerContent]}>
						<TysonCoachingPanel />
					</AppDrawerContent>
				)}
				{userSession.account.isLevitateSalesCoffeeAccount && (
					<AppDrawerContent value={COACHING_SETTINGS_DRAWER_VALUE} styles={[styleSheet.drawerContent]} unmountOnExit>
						<div className={css(styleSheet.settingsPanel)}>
							<div className={css(styleSheet.settingsSection)}>
								<h3 className={css(styleSheet.settingsPanelTitle)}>Realtime Coaching</h3>
								<Toggle
									id='realtime-coaching-toggle'
									isOn={realtimeCoachingEnabled}
									text='Enable AI coaching during calls'
									onToggleCheckChanged={checked => {
										updatePreferences.mutate({
											preferences: {
												coffeePreferences: { realtimeCoachingEnabled: checked, autoSuggestionsEnabled },
											},
										});
										if (!checked && isCoachingActive) {
											stopCoaching();
											appDrawerContext.setValue('');
										}
									}}
								/>
							</div>
							{realtimeCoachingEnabled && (
								<>
									<div className={css(styleSheet.settingsSectionDivider)}>
										<h3 className={css(styleSheet.settingsPanelTitle)}>Auto Suggestions</h3>
										<Toggle
											id='auto-suggestions-toggle'
											isOn={autoSuggestionsEnabled}
											text='Automatically suggest during calls'
											onToggleCheckChanged={checked => {
												updatePreferences.mutate({
													preferences: {
														coffeePreferences: { realtimeCoachingEnabled, autoSuggestionsEnabled: checked },
													},
												});
											}}
										/>
									</div>
									{coachingTemplates.length > 0 && (
										<div className={css(styleSheet.settingsSectionDivider)}>
											<h3 className={css(styleSheet.settingsPanelTitle)}>Choose AI Agent</h3>
											<Dropdown>
												<DropdownSelectTrigger styles={[styleSheet.settingsSelect]}>
													{coachingTemplates.find(t => t.id === selectedCoachingTemplateId)?.name || 'Default'}
												</DropdownSelectTrigger>
												<DropdownContent>
													<DropdownItem onClick={() => setSelectedCoachingTemplateId(null)}>Default</DropdownItem>
													{coachingTemplates.map(t => (
														<DropdownItem key={t.id} onClick={() => setSelectedCoachingTemplateId(t.id)}>
															{t.name}
														</DropdownItem>
													))}
												</DropdownContent>
											</Dropdown>
										</div>
									)}
								</>
							)}
						</div>
					</AppDrawerContent>
				)}
				<CoffeeDrawerToolbar>
					{showRealtimeCoachingUI && userSession.account.isLevitateSalesCoffeeAccount ? (
						<>
							<TysonCoachingDropdown
								disabled={!queue?.lead && !isCoachingActive && phoneCallStatus === DialerPhoneCallStatus.NoCall}
							/>
							{/* Real call coaching bridge - connects Twilio calls to coaching system */}
							<RealCallCoachingBridge />
						</>
					) : null}
					{userSession.account.isLevitateSalesCoffeeAccount && (
						<AppDrawerToolbarButton value={COACHING_SETTINGS_DRAWER_VALUE} tooltip='Coaching Settings'>
							<SettingsIcon stroke='currentColor' />
						</AppDrawerToolbarButton>
					)}
					<AppDrawerToolbarButton
						value={COFFEE_DRAWER_VALUE_DEAL}
						tooltip='Create/Update Deal'
						disabled={!queue?.lead}
						onClick={() => {
							if (queue.lead?.lastDealIsOpen) {
								queue.lead?.loadDealForm(queue.lead?.lastDeal?.id);
							} else {
								queue.lead?.createDealForm();
							}
							logInput('Open Deal Form', 'Click');
						}}
					>
						<DollarSignIcon />
					</AppDrawerToolbarButton>
				</CoffeeDrawerToolbar>
			</SideDrawer>
		</div>
	);
};

export const Queue = observer(QueueBase);
